import { useState, Fragment, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import { Add, Undo, ContentCopy, ContentPaste, Close, Info, CheckCircle, Error } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSnackbar } from "notistack";
import { useUnsavedChanges } from "../context/UnsavedChangesContext";
import { useOrganization } from "../context/OrganizationContext";
import Layout from "../components/layout/Layout";
import DateNavigator from "../components/common/DateNavigator";
import {
  CoverageTable,
  SchedulesDialog,
  SchedulesSidebar,
} from "../components/schedules";
import { useShops } from "../hooks/useShops";
import { useEmployees } from "../hooks/useEmployees";
import { useSchedules } from "../hooks/useSchedules";
import { employeesService } from "../services/firebase/employees.service";
import { Shop, CoverageRequirement } from "../types/shop.types";
import { Employee } from "../types/employee.types";
import { WorkingHours } from "../types/schedule.types";
import {
  ScheduleChangeKey,
  ScheduleChange,
  CopiedCellData,
  CopiedEmployeeWeekData,
  CopiedShopWeekData,
  CopiedEntireWeekData,
} from "../types/scheduleManager.types";
import {
  dayOfWeekOrder,
  dayLabels,
  formatDate,
  isToday,
  isPastDate,
  getWeekDates,
  getWeekNumber,
  formatDateRange,
  calculateEndTime,
  getChangeKey,
  getErrorKey,
} from "../utils/scheduleHelpers";
import { startTimeToMinutes, endTimeToMinutes } from "../utils/timeHelpers";
import { calculateAllCoverageGaps } from "../utils/coverageHelpers";

interface SchedulesPageProps {
  view?: "employee" | "coverage";
  readOnly?: boolean;
  skipLayout?: boolean;
}

export default function SchedulesPage({
  view = "employee",
  readOnly = false,
  skipLayout = false,
}: SchedulesPageProps) {
  const { shops, isLoading: isLoadingShops } = useShops();
  const { employees, isLoading: isLoadingEmployees } = useEmployees();
  const { enqueueSnackbar } = useSnackbar();
  const { organization } = useOrganization();

  // Get availability mode from organization settings (default to 'daily')
  const availabilityMode = organization?.settings?.availabilityMode || "daily";
  const isWeeklyMode = availabilityMode === "weekly";

  // Get edit history setting (default to false - no editing past dates)
  const allowEditHistory = organization?.settings?.allowEditHistory ?? false;

  // Check if editing is allowed for a specific date
  const canEditDate = (date: Date): boolean => {
    if (allowEditHistory) return true;
    return !isPastDate(date);
  };

  // Week navigation state (Monday-based)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : -(day - 1);
    const start = new Date(today);
    start.setDate(today.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  });

  // Extract month from weekStart for fetching schedules
  const currentMonth = `${weekStart.getFullYear()}-${String(
    weekStart.getMonth() + 1
  ).padStart(2, "0")}`;

  // Fetch schedules for all shops for the current month
  const {
    addOrUpdateEmployeeWorkingHours,
    bulkUpdateEmployeeWorkingHours,
    createSchedule,
    updateSchedule,
    getScheduleForDate,
    refetch,
  } = useSchedules("", currentMonth);

  // Working hours dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedWorkingHours, setSelectedWorkingHours] =
    useState<WorkingHours | null>(null); // For editing existing entry
  const [workingHoursForm, setWorkingHoursForm] = useState<WorkingHours>({
    id: "",
    startTime: "09:00",
    duration: 8,
  });

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Copy/Paste state
  const [copiedCellData, setCopiedCellData] = useState<CopiedCellData | null>(
    null
  );
  const [copiedEmployeeWeekData, setCopiedEmployeeWeekData] =
    useState<CopiedEmployeeWeekData | null>(null);
  const [copiedShopWeekData, setCopiedShopWeekData] =
    useState<CopiedShopWeekData | null>(null);
  const [copiedEntireWeekData, setCopiedEntireWeekData] =
    useState<CopiedEntireWeekData | null>(null);

  // Track working hours with validation errors (shopId_employeeId_dateKey_whId -> error message)
  const [workingHoursErrors, setWorkingHoursErrors] = useState<
    Record<string, string>
  >({});

  // Local state for tracking schedule changes
  const [localChanges, setLocalChanges] = useState<
    Record<ScheduleChangeKey, ScheduleChange>
  >({});
  const [originalState, setOriginalState] = useState<
    Record<ScheduleChangeKey, WorkingHours[]>
  >({});

  // UnsavedChanges context
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    return Object.keys(localChanges).length > 0;
  }, [localChanges]);

  // Get count of unsaved changes
  const getUnsavedChangesCount = useCallback((): number => {
    return Object.keys(localChanges).length;
  }, [localChanges]);

  // Check if there are any validation errors
  const hasErrors = useCallback((): boolean => {
    return Object.keys(workingHoursErrors).length > 0;
  }, [workingHoursErrors]);

  // Get count of errors
  const getErrorsCount = useCallback((): number => {
    return Object.keys(workingHoursErrors).length;
  }, [workingHoursErrors]);

  // Sync with UnsavedChangesContext
  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges());
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Get dates for the current week
  const weekDates = getWeekDates(weekStart);

  // Navigation handler for DateNavigator (converts string to Date)
  const handleDateChange = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    setWeekStart(date);
  };

  // Get employee's available hours for a specific date
  const getEmployeeAvailableHours = (
    employee: Employee,
    date: Date
  ): number => {
    const dateKey = formatDate(date);
    const availability = employeesService.getDateAvailability(
      employee,
      dateKey
    );
    if (!availability || availability.hoursAvailable <= 0) return 0;
    return availability.hoursAvailable;
  };

  // Get employee's scheduled hours for a specific date at a specific shop
  // Uses effective working hours (local changes override saved)
  const getEmployeeScheduledHoursAtShop = (
    employeeId: string,
    shopId: string,
    date: Date
  ): number => {
    const workingHours = getEmployeeWorkingHoursArray(employeeId, shopId, date);
    return workingHours.reduce((total, wh) => total + wh.duration, 0);
  };

  // Get employee's TOTAL scheduled hours for a specific date across ALL shops
  const getTotalEmployeeScheduledHours = (
    employeeId: string,
    date: Date
  ): number => {
    let totalHours = 0;

    // Sum hours across all shops
    shops.forEach((shop) => {
      totalHours += getEmployeeScheduledHoursAtShop(employeeId, shop.id, date);
    });

    return totalHours;
  };

  // Get employee's working hours array from saved data (Firebase)
  const getSavedWorkingHoursArray = (
    employeeId: string,
    shopId: string,
    date: Date
  ): WorkingHours[] => {
    const dateKey = formatDate(date);
    const schedule = getScheduleForDate(dateKey, shopId);
    if (
      !schedule ||
      !schedule.employeeSchedules ||
      !Array.isArray(schedule.employeeSchedules)
    ) {
      return [];
    }

    const employeeSchedule = schedule.employeeSchedules.find(
      (es) => es.employeeId === employeeId
    );
    if (!employeeSchedule || !Array.isArray(employeeSchedule.workingHours)) {
      return [];
    }

    return employeeSchedule.workingHours;
  };

  // Get employee's effective working hours (local changes override saved)
  const getEmployeeWorkingHoursArray = (
    employeeId: string,
    shopId: string,
    date: Date
  ): WorkingHours[] => {
    const dateKey = formatDate(date);
    const changeKey = getChangeKey(shopId, employeeId, dateKey);

    // Check if there's a local change for this cell
    if (localChanges[changeKey]) {
      return localChanges[changeKey].workingHours;
    }

    // Fall back to saved data
    return getSavedWorkingHoursArray(employeeId, shopId, date);
  };

  // Check if a cell has local changes
  const cellHasChanges = (
    shopId: string,
    employeeId: string,
    date: Date
  ): boolean => {
    const dateKey = formatDate(date);
    const changeKey = getChangeKey(shopId, employeeId, dateKey);
    return !!localChanges[changeKey];
  };

  // Check if a specific working hours entry is changed (new, modified, or in a changed cell)
  const isWorkingHoursChanged = (
    shopId: string,
    employeeId: string,
    date: Date,
    workingHoursId: string
  ): boolean => {
    const dateKey = formatDate(date);
    const changeKey = getChangeKey(shopId, employeeId, dateKey);

    // No changes for this cell
    if (!localChanges[changeKey]) return false;

    // Get original state
    const original =
      originalState[changeKey] ||
      getSavedWorkingHoursArray(employeeId, shopId, date);
    const current = localChanges[changeKey].workingHours;

    // Find this working hours in original
    const originalWh = original.find((wh) => wh.id === workingHoursId);
    const currentWh = current.find((wh) => wh.id === workingHoursId);

    // New entry (not in original)
    if (!originalWh && currentWh) return true;

    // Modified entry (different from original)
    if (originalWh && currentWh) {
      return JSON.stringify(originalWh) !== JSON.stringify(currentWh);
    }

    return false;
  };

  // Check if an employee row has any changes for the week
  const employeeRowHasChanges = (
    shopId: string,
    employeeId: string
  ): boolean => {
    return Object.keys(localChanges).some((key) => {
      const change = localChanges[key];
      return change.shopId === shopId && change.employeeId === employeeId;
    });
  };

  // Add or update a local change
  const addLocalChange = (
    shopId: string,
    employeeId: string,
    employeeName: string,
    date: Date,
    workingHours: WorkingHours[]
  ) => {
    const dateKey = formatDate(date);
    const changeKey = getChangeKey(shopId, employeeId, dateKey);

    // Store original state if not already stored
    if (!(changeKey in originalState)) {
      const savedHours = getSavedWorkingHoursArray(employeeId, shopId, date);
      setOriginalState((prev) => ({
        ...prev,
        [changeKey]: [...savedHours],
      }));
    }

    // Check if the new state matches the original
    const original =
      originalState[changeKey] ||
      getSavedWorkingHoursArray(employeeId, shopId, date);
    const isBackToOriginal =
      JSON.stringify(workingHours) === JSON.stringify(original);

    if (isBackToOriginal) {
      // Remove from local changes if back to original
      setLocalChanges((prev) => {
        const newChanges = { ...prev };
        delete newChanges[changeKey];
        return newChanges;
      });
      setOriginalState((prev) => {
        const newState = { ...prev };
        delete newState[changeKey];
        return newState;
      });
    } else {
      // Add/update local change
      setLocalChanges((prev) => ({
        ...prev,
        [changeKey]: {
          shopId,
          employeeId,
          employeeName,
          dateKey,
          workingHours,
        },
      }));
    }
  };

  // Revert a specific cell change
  const revertCellChange = (shopId: string, employeeId: string, date: Date) => {
    const dateKey = formatDate(date);
    const changeKey = getChangeKey(shopId, employeeId, dateKey);

    setLocalChanges((prev) => {
      const newChanges = { ...prev };
      delete newChanges[changeKey];
      return newChanges;
    });
    setOriginalState((prev) => {
      const newState = { ...prev };
      delete newState[changeKey];
      return newState;
    });
    // Clear errors for this cell (errorKey format: shopId_employeeId_dateKey_whId)
    setWorkingHoursErrors((prev) => {
      const newErrors = { ...prev };
      const errorPrefix = `${shopId}_${employeeId}_${dateKey}_`;
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(errorPrefix)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  // Revert all changes for an employee row
  const revertEmployeeRowChanges = (shopId: string, employeeId: string) => {
    setLocalChanges((prev) => {
      const newChanges = { ...prev };
      Object.keys(newChanges).forEach((key) => {
        const change = newChanges[key];
        if (change.shopId === shopId && change.employeeId === employeeId) {
          delete newChanges[key];
        }
      });
      return newChanges;
    });
    setOriginalState((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (key.startsWith(`${shopId}_${employeeId}_`)) {
          delete newState[key];
        }
      });
      return newState;
    });
    // Clear errors for this employee row (errorKey format: shopId_employeeId_dateKey_whId)
    setWorkingHoursErrors((prev) => {
      const newErrors = { ...prev };
      const errorPrefix = `${shopId}_${employeeId}_`;
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(errorPrefix)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  // Revert all changes
  const revertAllChanges = () => {
    setLocalChanges({});
    setOriginalState({});
    setWorkingHoursErrors({});
  };

  // Check if two time ranges overlap (handles overnight shifts)
  const doTimesOverlap = (
    start1: string,
    duration1: number,
    start2: string,
    duration2: number
  ): boolean => {
    const start1Minutes = startTimeToMinutes(start1);
    const end1Minutes = start1Minutes + duration1 * 60;
    const start2Minutes = startTimeToMinutes(start2);
    const end2Minutes = start2Minutes + duration2 * 60;

    // Standard overlap check
    const standardOverlap =
      start1Minutes < end2Minutes && start2Minutes < end1Minutes;

    // Handle overnight shifts: if shift 1 extends past midnight (end > 1440),
    // check if shift 2 falls within the next-day portion
    if (end1Minutes > 24 * 60) {
      // Shift 1 is overnight - check if shift 2 overlaps with the next-day portion
      // e.g., shift 1: 16:30-00:30 (end=1470), shift 2: 00:00-00:30 (start=0, end=30)
      // shift 2 should be seen as overlapping with the 00:00-00:30 portion of shift 1
      const overnightOverlap = start2Minutes < (end1Minutes - 24 * 60);
      if (overnightOverlap) return true;
    }

    // Handle reverse case: if shift 2 extends past midnight
    if (end2Minutes > 24 * 60) {
      const overnightOverlap = start1Minutes < (end2Minutes - 24 * 60);
      if (overnightOverlap) return true;
    }

    return standardOverlap;
  };

  // ============================================================================
  // CENTRALIZED VALIDATION: Single function to check ALL scheduling errors
  // ============================================================================
  // Validates all working hours for an employee on a specific date.
  // Checks: time restrictions, time conflicts, over-scheduling
  // Returns a map of errorKey -> error message
  //
  // IMPORTANT: Only shows errors on NEW (unsaved) entries, not on already saved ones.
  //
  // Optional workingHoursOverride: Use this to pass new working hours that haven't
  // been saved to state yet (since React state updates are async)
  const validateEmployeeScheduleForDate = (
    employee: Employee,
    date: Date,
    workingHoursOverride?: { shopId: string; workingHours: WorkingHours[] }
  ): Record<string, string> => {
    const errors: Record<string, string> = {};
    const dateKey = formatDate(date);

    // Collect all SAVED working hours IDs (to know which entries are new vs saved)
    const savedWhIds = new Set<string>();
    shops.forEach((shop) => {
      const savedWh = getSavedWorkingHoursArray(employee.id, shop.id, date);
      savedWh.forEach((wh) => savedWhIds.add(wh.id));
    });

    // Helper to check if an entry is new (unsaved)
    const isNewEntry = (whId: string) => !savedWhIds.has(whId);

    // Get all working hours for this employee on this date across all shops
    // Use override if provided for the specific shop
    const allWorkingHours: { shopId: string; shopName: string; workingHours: WorkingHours }[] = [];
    shops.forEach((shop) => {
      let workingHoursArray: WorkingHours[];
      if (workingHoursOverride && workingHoursOverride.shopId === shop.id) {
        // Use override for this shop
        workingHoursArray = workingHoursOverride.workingHours;
      } else {
        // Read from state/saved data
        workingHoursArray = getEmployeeWorkingHoursArray(employee.id, shop.id, date);
      }
      workingHoursArray.forEach((wh) => {
        allWorkingHours.push({
          shopId: shop.id,
          shopName: shop.name,
          workingHours: wh,
        });
      });
    });

    if (allWorkingHours.length === 0) {
      return errors; // No working hours, no errors
    }

    // Get employee availability for this date
    const availability = employeesService.getDateAvailability(employee, dateKey);

    // Check each working hours entry
    allWorkingHours.forEach((entry, index) => {
      const wh = entry.workingHours;
      const errorKey = getErrorKey(entry.shopId, employee.id, dateKey, wh.id);

      // Only show errors on NEW entries (not already saved)
      if (!isNewEntry(wh.id)) return;

      // 1. Check if employee is available on this day
      if (!availability || availability.hoursAvailable <= 0) {
        errors[errorKey] = "Employee not available on this day";
        return;
      }

      // 2. Check time restrictions
      if (availability.timeRestrictions) {
        const restrictions = availability.timeRestrictions;
        const [hours] = wh.startTime.split(":").map(Number);

        const hasAnyRestriction =
          restrictions.morning ||
          restrictions.afternoon ||
          restrictions.evening ||
          restrictions.night;

        if (hasAnyRestriction) {
          let isAllowed = false;
          // Morning: 6am - 12pm
          if (restrictions.morning && hours >= 6 && hours < 12) isAllowed = true;
          // Afternoon: 12pm - 6pm
          if (restrictions.afternoon && hours >= 12 && hours < 18) isAllowed = true;
          // Evening: 6pm - 12am
          if (restrictions.evening && hours >= 18 && hours < 24) isAllowed = true;
          // Night: 12am - 6am
          if (restrictions.night && hours >= 0 && hours < 6) isAllowed = true;

          if (!isAllowed) {
            const allowedPeriods: string[] = [];
            if (restrictions.morning) allowedPeriods.push("Morning");
            if (restrictions.afternoon) allowedPeriods.push("Afternoon");
            if (restrictions.evening) allowedPeriods.push("Evening");
            if (restrictions.night) allowedPeriods.push("Night");
            errors[errorKey] = `Time not allowed. Available: ${allowedPeriods.join(", ")}`;
            return;
          }
        }
      }

      // 3. Check for time conflicts with ALL other entries
      for (let j = 0; j < allWorkingHours.length; j++) {
        if (j === index) continue; // Skip self
        const other = allWorkingHours[j];
        if (doTimesOverlap(wh.startTime, wh.duration, other.workingHours.startTime, other.workingHours.duration)) {
          const otherEndTime = calculateEndTime(other.workingHours.startTime, other.workingHours.duration);
          // Only mark THIS (new) entry as error, not the other one
          errors[errorKey] = `Conflicts with ${other.shopName} (${other.workingHours.startTime}-${otherEndTime})`;
          break;
        }
      }
    });

    // 4. Check for over-scheduling
    // In daily mode: total hours > daily available hours
    // In weekly mode: check per-shop against coverage and weekly remaining
    const totalScheduled = allWorkingHours.reduce((sum, entry) => sum + entry.workingHours.duration, 0);

    if (!isWeeklyMode) {
      // Daily mode: check against daily availability
      const availableHours = availability?.hoursAvailable || 0;
      if (totalScheduled > availableHours) {
        const overBy = (totalScheduled - availableHours).toFixed(1);
        // Only mark NEW entries as over-scheduled (if not already marked with another error)
        allWorkingHours.forEach((entry) => {
          if (!isNewEntry(entry.workingHours.id)) return; // Skip saved entries
          const errorKey = getErrorKey(entry.shopId, employee.id, dateKey, entry.workingHours.id);
          if (!errors[errorKey]) {
            errors[errorKey] = `Over-scheduled by ${overBy}h`;
          }
        });
      }
    } else {
      // Weekly mode: check against weekly remaining and per-shop coverage
      // Calculate weekly remaining considering the override (state not updated yet)
      const weeklyAvailable = getEmployeeWeeklyAvailableHours(employee);

      // Get scheduled hours for OTHER days (from state)
      let weeklyScheduledOtherDays = 0;
      weekDates.forEach((d) => {
        if (formatDate(d) !== dateKey) {
          weeklyScheduledOtherDays += getTotalEmployeeScheduledHours(employee.id, d);
        }
      });

      // Total scheduled this week = other days (from state) + today (from allWorkingHours)
      const weeklyScheduledTotal = weeklyScheduledOtherDays + totalScheduled;
      const weeklyRemaining = weeklyAvailable - weeklyScheduledOtherDays;

      // Check per-shop coverage limits
      const shopScheduled: Record<string, number> = {};
      allWorkingHours.forEach((entry) => {
        shopScheduled[entry.shopId] = (shopScheduled[entry.shopId] || 0) + entry.workingHours.duration;
      });

      // Check each shop's coverage
      Object.entries(shopScheduled).forEach(([shopId, hoursInShop]) => {
        const shop = shops.find((s) => s.id === shopId);
        if (!shop) return;

        const shopCoverage = getShopCoverageHoursForDate(shop, date);
        const effectiveLimit = Math.min(weeklyRemaining, shopCoverage);

        if (hoursInShop > effectiveLimit) {
          const overBy = (hoursInShop - effectiveLimit).toFixed(1);
          // Mark NEW entries in this shop as over-scheduled
          allWorkingHours.forEach((entry) => {
            if (entry.shopId !== shopId) return;
            if (!isNewEntry(entry.workingHours.id)) return;
            const errorKey = getErrorKey(entry.shopId, employee.id, dateKey, entry.workingHours.id);
            if (!errors[errorKey]) {
              errors[errorKey] = `Over-scheduled by ${overBy}h`;
            }
          });
        }
      });

      // Also check total against weekly available
      if (weeklyScheduledTotal > weeklyAvailable) {
        const overBy = (weeklyScheduledTotal - weeklyAvailable).toFixed(1);
        allWorkingHours.forEach((entry) => {
          if (!isNewEntry(entry.workingHours.id)) return;
          const errorKey = getErrorKey(entry.shopId, employee.id, dateKey, entry.workingHours.id);
          if (!errors[errorKey]) {
            errors[errorKey] = `Exceeds weekly availability by ${overBy}h`;
          }
        });
      }
    }

    return errors;
  };

  // Recalculate all errors for an employee on a specific date and update state
  // workingHoursOverride: Pass new working hours that haven't been saved to state yet
  const recalculateErrorsForEmployeeDate = (
    employee: Employee,
    date: Date,
    workingHoursOverride?: { shopId: string; workingHours: WorkingHours[] }
  ) => {
    const dateKey = formatDate(date);
    const newErrors = validateEmployeeScheduleForDate(employee, date, workingHoursOverride);

    setWorkingHoursErrors((prev) => {
      const updated = { ...prev };

      // Remove old errors for this employee on this date
      Object.keys(updated).forEach((key) => {
        if (key.includes(`_${employee.id}_${dateKey}_`)) {
          delete updated[key];
        }
      });

      // Add new errors
      Object.assign(updated, newErrors);

      return updated;
    });

    return newErrors;
  };

  // Recalculate errors for multiple employees/dates (batch operation)
  // changesOverride: Map of "shopId_employeeId_dateKey" -> new working hours
  const recalculateErrorsForChanges = (
    changes: { employee: Employee; date: Date; shopId: string; workingHours: WorkingHours[] }[]
  ) => {
    const allNewErrors: Record<string, string> = {};
    const keysToRemove: string[] = [];

    changes.forEach(({ employee, date, shopId, workingHours }) => {
      const dateKey = formatDate(date);
      const newErrors = validateEmployeeScheduleForDate(employee, date, { shopId, workingHours });

      // Collect keys to remove (old errors for this employee/date)
      Object.keys(workingHoursErrors).forEach((key) => {
        if (key.includes(`_${employee.id}_${dateKey}_`)) {
          keysToRemove.push(key);
        }
      });

      // Collect new errors
      Object.assign(allNewErrors, newErrors);
    });

    setWorkingHoursErrors((prev) => {
      const updated = { ...prev };

      // Remove old errors
      keysToRemove.forEach((key) => delete updated[key]);

      // Add new errors
      Object.assign(updated, allNewErrors);

      return updated;
    });

    return allNewErrors;
  };

  // Get error for a specific working hours entry
  const getWorkingHoursError = (
    shopId: string,
    employeeId: string,
    dateKey: string,
    whId: string
  ): string | null => {
    const errorKey = getErrorKey(shopId, employeeId, dateKey, whId);
    return workingHoursErrors[errorKey] || null;
  };

  // Remove a specific working hours entry that has an error
  const removeWorkingHoursWithError = (
    shopId: string,
    employeeId: string,
    date: Date,
    whId: string
  ) => {
    // Get current working hours
    const currentWorkingHours = getEmployeeWorkingHoursArray(
      employeeId,
      shopId,
      date
    );
    const newWorkingHours = currentWorkingHours.filter((wh) => wh.id !== whId);

    // Find employee
    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return;

    // Update local changes
    addLocalChange(shopId, employeeId, employee.name, date, newWorkingHours);

    // Use centralized validation to recalculate ALL errors for this employee/date
    // Pass newWorkingHours since state hasn't updated yet
    recalculateErrorsForEmployeeDate(employee, date, {
      shopId,
      workingHours: newWorkingHours,
    });
  };

  // Helper to clear all clipboard data
  const clearAllClipboard = () => {
    setCopiedCellData(null);
    setCopiedEmployeeWeekData(null);
    setCopiedShopWeekData(null);
    setCopiedEntireWeekData(null);
  };

  // Copy cell (single day) data
  const handleCopyCell = (shop: Shop, employee: Employee, date: Date) => {
    const workingHours = getEmployeeWorkingHoursArray(
      employee.id,
      shop.id,
      date
    );
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // Monday = 0

    // Clear all other clipboard data first
    clearAllClipboard();

    setCopiedCellData({
      shopId: shop.id,
      employeeId: employee.id,
      employeeName: employee.name,
      workingHours: workingHours.map((wh) => ({ ...wh })), // Deep copy
      dayOfWeek,
      dateKey: formatDate(date),
    });

    enqueueSnackbar(
      `Copied ${employee.name}'s schedule for ${date.toLocaleDateString(
        "en-US",
        { weekday: "short", day: "numeric", month: "short" }
      )}`,
      { variant: "info" }
    );
  };

  // Paste cell data to a target cell
  const handlePasteCell = (shop: Shop, employee: Employee, date: Date) => {
    if (!copiedCellData) return;

    // Check if employee has remaining hours on this day
    const remainingHours = getRemainingHours(employee, date);
    if (remainingHours <= 0) return; // Skip if no remaining hours

    // Create new working hours with new IDs
    const newWorkingHours: WorkingHours[] = copiedCellData.workingHours.map(
      (wh) => ({
        ...wh,
        id: `wh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      })
    );

    // Get existing working hours for this shop (for merging)
    const existingWorkingHours = getEmployeeWorkingHoursArray(
      employee.id,
      shop.id,
      date
    );

    const mergedWorkingHours = [...existingWorkingHours, ...newWorkingHours];

    // Update local state first
    addLocalChange(
      shop.id,
      employee.id,
      employee.name,
      date,
      mergedWorkingHours
    );

    // Use centralized validation to check all errors
    // Pass mergedWorkingHours since state hasn't updated yet
    const errors = recalculateErrorsForEmployeeDate(employee, date, {
      shopId: shop.id,
      workingHours: mergedWorkingHours,
    });

    const errorCount = Object.keys(errors).length;
    if (errorCount > 0) {
      enqueueSnackbar(
        `Pasted schedule with ${errorCount} conflict${
          errorCount > 1 ? "s" : ""
        }. Review and remove invalid entries.`,
        { variant: "warning" }
      );
    } else {
      enqueueSnackbar("Schedule pasted successfully", { variant: "info" });
    }
  };

  // Copy entire employee's week schedule
  const handleCopyEmployeeWeek = (shop: Shop, employee: Employee) => {
    const weekData: Record<string, WorkingHours[]> = {};

    weekDates.forEach((date) => {
      const dateKey = formatDate(date);
      const workingHours = getEmployeeWorkingHoursArray(
        employee.id,
        shop.id,
        date
      );
      if (workingHours.length > 0) {
        weekData[dateKey] = workingHours.map((wh) => ({ ...wh })); // Deep copy
      }
    });

    const weekStartDate = formatDate(weekDates[0]);
    const weekEndDate = formatDate(weekDates[6]);
    const weekNum = getWeekNumber(weekDates[0]);

    // Clear all other clipboard data first
    clearAllClipboard();

    setCopiedEmployeeWeekData({
      shopId: shop.id,
      employeeId: employee.id,
      employeeName: employee.name,
      weekData,
      weekStartDate,
      weekEndDate,
      weekNumber: weekNum,
    });

    enqueueSnackbar(
      `Copied ${employee.name}'s week (${formatDateRange(weekStartDate, weekEndDate)})`,
      { variant: "info" }
    );
  };

  // Paste employee's week schedule to another employee
  const handlePasteEmployeeWeek = (shop: Shop, employee: Employee) => {
    if (!copiedEmployeeWeekData) return;

    const affectedChanges: { employee: Employee; date: Date; shopId: string; workingHours: WorkingHours[] }[] = [];
    let skippedDays = 0;

    // Calculate day offset (in case source and target weeks differ)
    weekDates.forEach((date, index) => {
      // Check if employee has remaining hours on this day
      const remainingHours = getRemainingHours(employee, date);
      if (remainingHours <= 0) {
        skippedDays++;
        return; // Skip if no remaining hours
      }

      // Get source date from copied data based on day index
      const sourceKeys = Object.keys(copiedEmployeeWeekData.weekData);
      // Find matching day by index
      const sourceDateKey = sourceKeys.find((key) => {
        const sourceDate = new Date(key);
        const sourceDayOfWeek =
          sourceDate.getDay() === 0 ? 6 : sourceDate.getDay() - 1;
        return sourceDayOfWeek === index;
      });

      if (sourceDateKey && copiedEmployeeWeekData.weekData[sourceDateKey]) {
        const sourceWorkingHours =
          copiedEmployeeWeekData.weekData[sourceDateKey];

        // Create new working hours with new IDs
        const newWorkingHours: WorkingHours[] = sourceWorkingHours.map(
          (wh) => ({
            ...wh,
            id: `wh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          })
        );

        // Update local state
        addLocalChange(
          shop.id,
          employee.id,
          employee.name,
          date,
          newWorkingHours
        );

        affectedChanges.push({ employee, date, shopId: shop.id, workingHours: newWorkingHours });
      }
    });

    // Use centralized validation to check all errors for affected dates
    const allErrors = recalculateErrorsForChanges(affectedChanges);

    const errorCount = Object.keys(allErrors).length;
    let message = "Week schedule pasted";
    if (skippedDays > 0) {
      message += `, ${skippedDays} day${skippedDays > 1 ? "s" : ""} skipped (no hours left)`;
    }
    if (errorCount > 0) {
      message += `, ${errorCount} conflict${errorCount > 1 ? "s" : ""}`;
      enqueueSnackbar(message, { variant: "warning" });
    } else {
      enqueueSnackbar(message, { variant: "info" });
    }
  };

  // Copy shop's entire week schedule (all employees)
  const handleCopyShopWeek = (shop: Shop) => {
    const shopEmployees = employees.filter(
      (emp) => emp.status === "active" && emp.assignedShopIds.includes(shop.id)
    );

    const employeeSchedules: CopiedShopWeekData["employeeSchedules"] = {};

    shopEmployees.forEach((employee) => {
      const weekData: Record<string, WorkingHours[]> = {};

      weekDates.forEach((date) => {
        const dateKey = formatDate(date);
        const workingHours = getEmployeeWorkingHoursArray(
          employee.id,
          shop.id,
          date
        );
        if (workingHours.length > 0) {
          weekData[dateKey] = workingHours.map((wh) => ({ ...wh }));
        }
      });

      if (Object.keys(weekData).length > 0) {
        employeeSchedules[employee.id] = {
          employeeId: employee.id,
          employeeName: employee.name,
          weekData,
        };
      }
    });

    const weekStartDate = formatDate(weekDates[0]);
    const weekEndDate = formatDate(weekDates[6]);
    const weekNum = getWeekNumber(weekDates[0]);

    // Clear all other clipboard data first
    clearAllClipboard();

    setCopiedShopWeekData({
      shopId: shop.id,
      shopName: shop.name,
      employeeSchedules,
      weekStartDate,
      weekEndDate,
      weekNumber: weekNum,
    });

    enqueueSnackbar(
      `Copied ${shop.name} (Week ${weekNum}: ${formatDateRange(weekStartDate, weekEndDate)})`,
      { variant: "info" }
    );
  };

  // Paste shop week to another shop (only employees assigned to target shop)
  const handlePasteShopWeek = (targetShop: Shop) => {
    if (!copiedShopWeekData) return;

    const targetShopEmployees = employees.filter(
      (emp) =>
        emp.status === "active" && emp.assignedShopIds.includes(targetShop.id)
    );

    const affectedChanges: { employee: Employee; date: Date; shopId: string; workingHours: WorkingHours[] }[] = [];
    let pastedCount = 0;
    let skippedCount = 0;
    let skippedDays = 0;

    // For each employee in copied data, check if they're assigned to target shop
    Object.values(copiedShopWeekData.employeeSchedules).forEach(
      (empSchedule) => {
        const targetEmployee = targetShopEmployees.find(
          (emp) => emp.id === empSchedule.employeeId
        );

        if (!targetEmployee) {
          // Employee not assigned to target shop - skip
          skippedCount++;
          return;
        }

        pastedCount++;

        // Paste week data for this employee
        weekDates.forEach((date, index) => {
          // Check if employee has remaining hours on this day
          const remainingHours = getRemainingHours(targetEmployee, date);
          if (remainingHours <= 0) {
            skippedDays++;
            return; // Skip if no remaining hours
          }

          const sourceKeys = Object.keys(empSchedule.weekData);
          const sourceDateKey = sourceKeys.find((key) => {
            const sourceDate = new Date(key);
            const sourceDayOfWeek =
              sourceDate.getDay() === 0 ? 6 : sourceDate.getDay() - 1;
            return sourceDayOfWeek === index;
          });

          if (sourceDateKey && empSchedule.weekData[sourceDateKey]) {
            const sourceWorkingHours = empSchedule.weekData[sourceDateKey];

            const newWorkingHours: WorkingHours[] = sourceWorkingHours.map(
              (wh) => ({
                ...wh,
                id: `wh_${Date.now()}_${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
              })
            );

            // Update local state
            addLocalChange(
              targetShop.id,
              targetEmployee.id,
              targetEmployee.name,
              date,
              newWorkingHours
            );

            affectedChanges.push({ employee: targetEmployee, date, shopId: targetShop.id, workingHours: newWorkingHours });
          }
        });
      }
    );

    // Use centralized validation to check all errors
    const allErrors = recalculateErrorsForChanges(affectedChanges);

    const errorCount = Object.keys(allErrors).length;
    let message = `Pasted to ${pastedCount} employee${
      pastedCount !== 1 ? "s" : ""
    }`;
    if (skippedCount > 0) {
      message += `, ${skippedCount} skipped (not in shop)`;
    }
    if (skippedDays > 0) {
      message += `, ${skippedDays} day${skippedDays > 1 ? "s" : ""} skipped (no hours left)`;
    }
    if (errorCount > 0) {
      message += `, ${errorCount} conflict${errorCount !== 1 ? "s" : ""}`;
      enqueueSnackbar(message, { variant: "warning" });
    } else {
      enqueueSnackbar(message, { variant: "info" });
    }
  };

  // Copy entire week (all shops, all employees)
  const handleCopyEntireWeek = () => {
    clearAllClipboard();

    const weekStartDate = formatDate(weekDates[0]);
    const weekEndDate = formatDate(weekDates[6]);
    const weekNumber = getWeekNumber(weekDates[0]);

    const allShopsData: Record<string, CopiedShopWeekData> = {};

    shops.forEach((shop) => {
      const shopEmployees = employees.filter(
        (emp) =>
          emp.status === "active" && emp.assignedShopIds.includes(shop.id)
      );

      const employeeSchedules: CopiedShopWeekData["employeeSchedules"] = {};

      shopEmployees.forEach((employee) => {
        const weekData: Record<string, WorkingHours[]> = {};

        weekDates.forEach((date) => {
          const dateKey = formatDate(date);
          const workingHours = getEmployeeWorkingHoursArray(
            employee.id,
            shop.id,
            date
          );
          if (workingHours.length > 0) {
            weekData[dateKey] = workingHours.map((wh) => ({ ...wh }));
          }
        });

        if (Object.keys(weekData).length > 0) {
          employeeSchedules[employee.id] = {
            employeeId: employee.id,
            employeeName: employee.name,
            weekData,
          };
        }
      });

      if (Object.keys(employeeSchedules).length > 0) {
        allShopsData[shop.id] = {
          shopId: shop.id,
          shopName: shop.name,
          employeeSchedules,
          weekStartDate,
          weekEndDate,
          weekNumber,
        };
      }
    });

    setCopiedEntireWeekData({
      shops: allShopsData,
      weekStartDate,
      weekEndDate,
      weekNumber,
    });

    const shopCount = Object.keys(allShopsData).length;
    enqueueSnackbar(
      `Copied entire week (${shopCount} shop${shopCount !== 1 ? "s" : ""})`,
      { variant: "info" }
    );
  };

  // Paste entire week (all shops, all employees)
  const handlePasteEntireWeek = () => {
    if (!copiedEntireWeekData) return;

    const affectedChanges: { employee: Employee; date: Date; shopId: string; workingHours: WorkingHours[] }[] = [];
    let totalPasted = 0;
    let skippedDays = 0;

    Object.values(copiedEntireWeekData.shops).forEach((shopData) => {
      const targetShop = shops.find((s) => s.id === shopData.shopId);
      if (!targetShop) return;

      const targetShopEmployees = employees.filter(
        (emp) =>
          emp.status === "active" && emp.assignedShopIds.includes(targetShop.id)
      );

      Object.values(shopData.employeeSchedules).forEach((empSchedule) => {
        const targetEmployee = targetShopEmployees.find(
          (emp) => emp.id === empSchedule.employeeId
        );

        if (!targetEmployee) return;

        totalPasted++;

        weekDates.forEach((date, index) => {
          // Check if employee has remaining hours on this day
          const remainingHours = getRemainingHours(targetEmployee, date);
          if (remainingHours <= 0) {
            skippedDays++;
            return; // Skip if no remaining hours
          }

          const sourceKeys = Object.keys(empSchedule.weekData);
          const sourceDateKey = sourceKeys.find((key) => {
            const sourceDate = new Date(key);
            const sourceDayOfWeek =
              sourceDate.getDay() === 0 ? 6 : sourceDate.getDay() - 1;
            return sourceDayOfWeek === index;
          });

          if (sourceDateKey && empSchedule.weekData[sourceDateKey]) {
            const sourceWorkingHours = empSchedule.weekData[sourceDateKey];

            const newWorkingHours: WorkingHours[] = sourceWorkingHours.map(
              (wh) => ({
                ...wh,
                id: `wh_${Date.now()}_${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
              })
            );

            addLocalChange(
              targetShop.id,
              targetEmployee.id,
              targetEmployee.name,
              date,
              newWorkingHours
            );

            affectedChanges.push({ employee: targetEmployee, date, shopId: targetShop.id, workingHours: newWorkingHours });
          }
        });
      });
    });

    // Use centralized validation to check all errors
    const allErrors = recalculateErrorsForChanges(affectedChanges);

    const errorCount = Object.keys(allErrors).length;
    let message = `Pasted entire week (${totalPasted} schedules)`;
    if (skippedDays > 0) {
      message += `, ${skippedDays} day${skippedDays > 1 ? "s" : ""} skipped (no hours left)`;
    }
    if (errorCount > 0) {
      message += `, ${errorCount} conflict${errorCount !== 1 ? "s" : ""}`;
      enqueueSnackbar(message, { variant: "warning" });
    } else {
      enqueueSnackbar(message, { variant: "info" });
    }
  };

  // Save all changes to Firebase using bulk updates
  const [isSaving, setIsSaving] = useState(false);
  const saveAllChanges = async () => {
    if (Object.keys(localChanges).length === 0) return;

    setIsSaving(true);
    try {
      // Group changes by schedule (shopId + dateKey)
      const scheduleGroups: Record<
        string,
        { shopId: string; dateKey: string; changes: ScheduleChange[] }
      > = {};

      Object.values(localChanges).forEach((change) => {
        const scheduleKey = `${change.shopId}_${change.dateKey}`;
        if (!scheduleGroups[scheduleKey]) {
          scheduleGroups[scheduleKey] = {
            shopId: change.shopId,
            dateKey: change.dateKey,
            changes: [],
          };
        }
        scheduleGroups[scheduleKey].changes.push(change);
      });

      // Process each schedule with bulk updates
      for (const group of Object.values(scheduleGroups)) {
        let schedule = getScheduleForDate(group.dateKey, group.shopId);
        let scheduleId: string;

        // Create schedule if it doesn't exist
        if (!schedule) {
          scheduleId = await createSchedule({
            shopId: group.shopId,
            date: group.dateKey,
            employeeSchedules: [],
            availabilityMode,
          });
          await refetch();
          schedule = getScheduleForDate(group.dateKey, group.shopId);
        } else {
          scheduleId = schedule.id;
          // Update existing schedule with current availability mode
          await updateSchedule(scheduleId, { availabilityMode });
        }

        // Prepare bulk update - collect all employee changes for this schedule
        const employeeChanges = group.changes.map((change) => ({
          employeeId: change.employeeId,
          employeeName: change.employeeName,
          workingHours: change.workingHours,
        }));

        // Single bulk update for all employees in this schedule
        await bulkUpdateEmployeeWorkingHours(scheduleId, employeeChanges);
      }

      await refetch();

      // Clear local changes
      setLocalChanges({});
      setOriginalState({});

      enqueueSnackbar("All changes saved successfully!", {
        variant: "success",
      });
    } catch (err) {
      const message =
        (err as Error)?.message || "Failed to save changes";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Get coveragePeriods for a specific date (check date-specific first, then default)
  const getCoverageForDate = (
    shop: Shop,
    date: Date
  ): CoverageRequirement[] => {
    const dateKey = formatDate(date);

    // Check if there are date-specific coveragePeriods
    if (shop.dateCoverage && shop.dateCoverage[dateKey]) {
      return shop.dateCoverage[dateKey];
    }

    // Fall back to default coveragePeriods for the day of week
    const dayOfWeek =
      dayOfWeekOrder[date.getDay() === 0 ? 6 : date.getDay() - 1];
    return shop.defaultCoverage?.[dayOfWeek] || [];
  };

  // ============================================================================
  // WEEKLY AVAILABILITY MODE HELPERS
  // ============================================================================

  // Get employee's total available hours for the entire week
  const getEmployeeWeeklyAvailableHours = (employee: Employee): number => {
    let totalHours = 0;
    weekDates.forEach((date) => {
      totalHours += getEmployeeAvailableHours(employee, date);
    });
    return totalHours;
  };

  // Get employee's total scheduled hours for the entire week across ALL shops
  const getEmployeeWeeklyScheduledHours = (employeeId: string): number => {
    let totalHours = 0;
    weekDates.forEach((date) => {
      totalHours += getTotalEmployeeScheduledHours(employeeId, date);
    });
    return totalHours;
  };

  // Get employee's remaining weekly hours (weekly available - weekly scheduled)
  const getEmployeeWeeklyRemainingHours = (employee: Employee): number => {
    const weeklyAvailable = getEmployeeWeeklyAvailableHours(employee);
    const weeklyScheduled = getEmployeeWeeklyScheduledHours(employee.id);
    return weeklyAvailable - weeklyScheduled;
  };

  // Get total coverage hours for a specific date for a specific shop
  const getShopCoverageHoursForDate = (shop: Shop, date: Date): number => {
    let totalCoverageHours = 0;

    const coverageReqs = getCoverageForDate(shop, date);
    coverageReqs.forEach((req) => {
      // Calculate duration of each coverage period
      const [startH, startM] = req.startTime.split(":").map(Number);
      const [endH, endM] = req.endTime.split(":").map(Number);
      let startMinutes = startH * 60 + startM;
      let endMinutes = endH * 60 + endM;

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60;
      }

      const durationHours = (endMinutes - startMinutes) / 60;
      totalCoverageHours += durationHours;
    });

    return totalCoverageHours;
  };

  // Get total coverage hours for a specific date across ALL shops the employee is assigned to
  const getTotalCoverageHoursForDate = (employee: Employee, date: Date): number => {
    let totalCoverageHours = 0;

    shops.forEach((shop) => {
      // Only count if employee is assigned to this shop
      if (!employee.assignedShopIds.includes(shop.id)) return;
      totalCoverageHours += getShopCoverageHoursForDate(shop, date);
    });

    return totalCoverageHours;
  };

  // Get effective daily remaining hours based on availability mode
  // If shop is provided, use that shop's coverage; otherwise use total across all shops
  const getEffectiveDailyRemaining = (employee: Employee, date: Date, shop?: Shop): number => {
    const dailyScheduled = getTotalEmployeeScheduledHours(employee.id, date);
    const dailyAvailable = getEmployeeAvailableHours(employee, date);

    // If employee is off (0 availability) on this day, they can't work
    if (dailyAvailable <= 0) {
      return -dailyScheduled; // Return negative if already scheduled (over-scheduled)
    }

    if (!isWeeklyMode) {
      // Daily mode: daily available - daily scheduled
      return dailyAvailable - dailyScheduled;
    }

    // Weekly mode:
    // Effective limit = min(remaining weekly, coverage for that day - already scheduled today)
    const weeklyRemaining = getEmployeeWeeklyRemainingHours(employee);

    // Use specific shop's coverage if provided, otherwise total across all shops
    const coverageHours = shop
      ? getShopCoverageHoursForDate(shop, date)
      : getTotalCoverageHoursForDate(employee, date);

    // If no coverage defined, employee can't work that day
    if (coverageHours <= 0) {
      return -dailyScheduled; // Return negative if already scheduled (over-scheduled)
    }

    // Remaining coverage for today (coverage - already scheduled)
    const remainingCoverage = coverageHours - dailyScheduled;

    // Remaining = min(weekly remaining, remaining coverage for today)
    return Math.min(weeklyRemaining, remainingCoverage);
  };

  // Get remaining available hours for employee
  // Uses weekly or daily mode based on organization settings
  // If shop is provided, use that shop's coverage for weekly mode
  const getRemainingHours = (employee: Employee, date: Date, shop?: Shop): number => {
    return getEffectiveDailyRemaining(employee, date, shop);
  };

  // ============================================================================

  // Check if working hours overlap with a coverage period
  const doesWorkingHoursOverlapCoverage = (
    workingHours: WorkingHours,
    shift: CoverageRequirement
  ): boolean => {
    const whStartMinutes = startTimeToMinutes(workingHours.startTime);
    const whEndMinutes = whStartMinutes + workingHours.duration * 60;

    const shiftStartMinutes = startTimeToMinutes(shift.startTime);
    let shiftEndMinutes = endTimeToMinutes(shift.endTime);

    // Handle overnight coverage periods (e.g., 16:30 to 00:30)
    if (shiftEndMinutes <= shiftStartMinutes) {
      shiftEndMinutes += 24 * 60;
    }

    // Check if there's any overlap
    return whStartMinutes < shiftEndMinutes && whEndMinutes > shiftStartMinutes;
  };

  // Calculate coverage for a shift on a specific date
  const calculateCoveragePeriodCoverage = (
    shop: Shop,
    shift: CoverageRequirement,
    date: Date
  ): number => {
    const dateKey = formatDate(date);
    const schedule = getScheduleForDate(dateKey, shop.id);

    if (
      !schedule ||
      !schedule.employeeSchedules ||
      !Array.isArray(schedule.employeeSchedules)
    ) {
      return 0;
    }

    let coverageCount = 0;

    // Count employees whose working hours overlap with this shift
    schedule.employeeSchedules.forEach((empSchedule) => {
      if (Array.isArray(empSchedule.workingHours)) {
        // Check if any of the employee's working hours overlap with this shift
        const hasOverlap = empSchedule.workingHours.some((wh) =>
          doesWorkingHoursOverlapCoverage(wh, shift)
        );
        if (hasOverlap) {
          coverageCount++;
        }
      }
    });

    return coverageCount;
  };

  // Get shift with lowest coverage (that's below min coverage)
  const getCoverageWithLowestStaffing = (
    shop: Shop,
    date: Date
  ): CoverageRequirement | null => {
    const coveragePeriods = getCoverageForDate(shop, date);

    let lowestShift: CoverageRequirement | null = null;
    let lowestCoverageRatio = Infinity;

    coveragePeriods.forEach((shift) => {
      const coverage = calculateCoveragePeriodCoverage(shop, shift, date);
      const coverageRatio = coverage / shift.minCoverage;

      if (coverage < shift.minCoverage && coverageRatio < lowestCoverageRatio) {
        lowestCoverageRatio = coverageRatio;
        lowestShift = shift;
      }
    });

    return lowestShift;
  };

  // Calculate coverage gaps for a shop on a specific date (uses runtime data including unsaved changes)
  const getShopDayCoverageGaps = (
    shop: Shop,
    date: Date
  ): { timeSlot: string; shortage: number; required: number }[] => {
    const coveragePeriods = getCoverageForDate(shop, date);

    // Get all employees assigned to this shop
    const shopEmployees = employees.filter(
      (emp) => emp.status === "active" && emp.assignedShopIds.includes(shop.id)
    );

    // Collect effective working hours (including unsaved changes) for all employees
    const allWorkingHours: WorkingHours[] = [];
    shopEmployees.forEach((emp) => {
      const empWorkingHours = getEmployeeWorkingHoursArray(emp.id, shop.id, date);
      allWorkingHours.push(...empWorkingHours);
    });

    // Use centralized coverage gap calculation
    const gaps = calculateAllCoverageGaps(coveragePeriods, allWorkingHours);

    // Convert to the expected format
    return gaps.map((gap) => ({
      timeSlot: `${gap.startTime}-${gap.endTime}`,
      shortage: gap.shortage,
      required: gap.required,
    }));
  };

  // Get employee's last scheduled end time for a specific date across ALL shops
  const getEmployeeLastEndTime = (
    employeeId: string,
    date: Date
  ): string | null => {
    let latestEndMinutes = 0;

    // Check all shops for this employee's schedules
    shops.forEach((shop) => {
      const workingHoursArray = getEmployeeWorkingHoursArray(
        employeeId,
        shop.id,
        date
      );

      workingHoursArray.forEach((wh) => {
        const [startHour, startMin] = wh.startTime.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = startMinutes + wh.duration * 60;

        if (endMinutes > latestEndMinutes) {
          latestEndMinutes = endMinutes;
        }
      });
    });

    if (latestEndMinutes === 0) {
      return null; // No schedules found
    }

    // Convert back to HH:mm format
    const hours = Math.floor(latestEndMinutes / 60) % 24;
    const minutes = latestEndMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  };

  // Get default start time based on employee's existing schedules and coverage needs
  const getDefaultStartTime = (
    shop: Shop,
    employee: Employee,
    date: Date
  ): string => {
    // First, check if employee has existing schedules for this date
    const lastEndTime = getEmployeeLastEndTime(employee.id, date);
    if (lastEndTime) {
      // Round up to next 30-minute interval
      const [hours, minutes] = lastEndTime.split(":").map(Number);
      let totalMinutes = hours * 60 + minutes;

      // Round up to next 30-minute slot
      if (totalMinutes % 30 !== 0) {
        totalMinutes = Math.ceil(totalMinutes / 30) * 30;
      }

      const roundedHours = Math.floor(totalMinutes / 60) % 24;
      const roundedMinutes = totalMinutes % 60;
      return `${String(roundedHours).padStart(2, "0")}:${String(
        roundedMinutes
      ).padStart(2, "0")}`;
    }

    // No existing schedules, use coverage needs
    const shiftWithLowestCoverage = getCoverageWithLowestStaffing(shop, date);

    // If there's a shift below min coverage, use its start time
    if (shiftWithLowestCoverage) {
      return shiftWithLowestCoverage.startTime;
    }

    // Otherwise, get the shop's opening time for that date
    const dateKey = formatDate(date);
    const dayOfWeek =
      dayOfWeekOrder[date.getDay() === 0 ? 6 : date.getDay() - 1];

    // Check for date-specific hours first
    const dateHours = shop.hoursConfig[dateKey];
    if (dateHours) {
      return dateHours.openTime;
    }

    // Fall back to default hours for the day of week
    const defaultHours = shop.defaultHours[dayOfWeek];
    return defaultHours?.openTime || "09:00";
  };

  // Check if start time is valid according to employee's time restrictions
  const validateStartTimeWithRestrictions = (
    employee: Employee,
    date: Date,
    startTime: string
  ): { valid: boolean; message?: string } => {
    const dateKey = formatDate(date);
    const availability = employeesService.getDateAvailability(
      employee,
      dateKey
    );

    if (!availability || !availability.timeRestrictions) {
      return { valid: true }; // No restrictions
    }

    const restrictions = availability.timeRestrictions;
    const [hours] = startTime.split(":").map(Number);

    // Check which periods are enabled
    const hasAnyRestriction =
      restrictions.morning ||
      restrictions.afternoon ||
      restrictions.evening ||
      restrictions.night;
    if (!hasAnyRestriction) {
      return { valid: true }; // No restrictions set
    }

    // Check if start time falls within allowed periods
    // Morning: 6am - 12pm (6-12)
    // Afternoon: 12pm - 6pm (12-18)
    // Evening: 6pm - 12am (18-24)
    // Night: 12am - 6am (0-6)

    if (restrictions.morning && hours >= 6 && hours < 12) {
      return { valid: true };
    }
    if (restrictions.afternoon && hours >= 12 && hours < 18) {
      return { valid: true };
    }
    if (restrictions.evening && hours >= 18 && hours < 24) {
      return { valid: true };
    }
    if (restrictions.night && hours >= 0 && hours < 6) {
      return { valid: true };
    }

    // Build error message showing allowed periods
    const allowedPeriods: string[] = [];
    if (restrictions.morning) allowedPeriods.push("Morning (6am-12pm)");
    if (restrictions.afternoon) allowedPeriods.push("Afternoon (12pm-6pm)");
    if (restrictions.evening) allowedPeriods.push("Evening (6pm-12am)");
    if (restrictions.night) allowedPeriods.push("Night (12am-6am)");

    return {
      valid: false,
      message: `Employee only available during: ${allowedPeriods.join(", ")}`,
    };
  };

  // Calculate optimal duration based on start time for a specific shop/employee/date
  const calculateOptimalDurationFor = (
    startTime: string,
    shop: Shop,
    employee: Employee,
    date: Date,
    existingDuration?: number
  ): number => {
    // Get available hours (pass shop for accurate weekly mode calculation)
    const maxAvailableHours =
      getRemainingHours(employee, date, shop) + (existingDuration || 0);

    // Get shop hours
    const dateKey = formatDate(date);
    const dayOfWeek =
      dayOfWeekOrder[date.getDay() === 0 ? 6 : date.getDay() - 1];
    const dateHours = shop.hoursConfig?.[dateKey];
    const shopHours = dateHours || shop.defaultHours[dayOfWeek];

    if (!shopHours || !shopHours.isOpen) {
      return maxAvailableHours;
    }

    // Calculate hours until shop closes
    const [startHour, startMin] = startTime.split(":").map(Number);
    let startMinutes = startHour * 60 + startMin;

    const [closeHour, closeMin] = shopHours.closeTime.split(":").map(Number);
    const [openHour, openMin] = shopHours.openTime.split(":").map(Number);
    let closeMinutes = closeHour * 60 + closeMin;
    const openMinutes = openHour * 60 + openMin;

    // Handle shops that close after midnight
    if (closeMinutes <= openMinutes) {
      closeMinutes += 24 * 60;
      // If start time is in early morning (before 6am), treat it as next day
      if (startMinutes < 360) {
        startMinutes += 24 * 60;
      }
    }

    const hoursUntilClose = (closeMinutes - startMinutes) / 60;

    // Return minimum of available hours and hours until close
    return Math.min(maxAvailableHours, Math.max(0, hoursUntilClose));
  };

  // Handle open add/edit working hours dialog
  const handleOpenDialog = (
    shop: Shop,
    employee: Employee,
    date: Date,
    existingWorkingHours?: WorkingHours
  ) => {
    setSelectedShop(shop);
    setSelectedEmployee(employee);
    setSelectedDate(date);

    if (existingWorkingHours) {
      // Editing existing entry
      setSelectedWorkingHours(existingWorkingHours);
      setWorkingHoursForm(existingWorkingHours);
    } else {
      // Adding new entry
      setSelectedWorkingHours(null);
      const defaultStart = getDefaultStartTime(shop, employee, date);
      const optimalDuration = calculateOptimalDurationFor(
        defaultStart,
        shop,
        employee,
        date
      );
      setWorkingHoursForm({
        id: `wh_${Date.now()}`, // Generate unique ID
        startTime: defaultStart,
        duration: optimalDuration,
      });
    }

    setDialogOpen(true);
  };

  // Validate working hours form
  const validateWorkingHoursForm = (): {
    isValid: boolean;
    startTimeError?: string;
    durationError?: string;
  } => {
    if (!selectedShop || !selectedEmployee || !selectedDate) {
      return { isValid: false };
    }

    let startTimeError: string | undefined;
    let durationError: string | undefined;

    // Validate start time with employee's time restrictions
    const timeRestrictionValidation = validateStartTimeWithRestrictions(
      selectedEmployee,
      selectedDate,
      workingHoursForm.startTime
    );
    if (!timeRestrictionValidation.valid) {
      startTimeError = timeRestrictionValidation.message;
    }

    // Validate start time is within shop coverage hours
    const dateKey = formatDate(selectedDate);
    const dayOfWeek =
      dayOfWeekOrder[
        selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1
      ];
    const dateHours = selectedShop.hoursConfig?.[dateKey];
    const shopHours = dateHours || selectedShop.defaultHours[dayOfWeek];

    if (shopHours && shopHours.isOpen) {
      const [startHour, startMin] = workingHoursForm.startTime
        .split(":")
        .map(Number);
      let startMinutes = startHour * 60 + startMin;

      const [openHour, openMin] = shopHours.openTime.split(":").map(Number);
      const [closeHour, closeMin] = shopHours.closeTime.split(":").map(Number);
      let openMinutes = openHour * 60 + openMin;
      let closeMinutes = closeHour * 60 + closeMin;

      // Handle shops that close after midnight
      if (closeMinutes <= openMinutes) {
        closeMinutes += 24 * 60;
        // If start time is in early morning (before 6am), treat it as next day
        if (startMinutes < 360) {
          startMinutes += 24 * 60;
        }
      }

      // Check if start time is before shop opens
      if (startMinutes < openMinutes) {
        startTimeError = `Shop opens at ${shopHours.openTime}`;
      }
      // Check if start time is at or after shop closes
      else if (startMinutes >= closeMinutes) {
        startTimeError = `Shop closes at ${shopHours.closeTime}`;
      }
    }

    // Validate duration doesn't exceed available hours (pass shop for accurate weekly mode calculation)
    const maxAvailableHours =
      getRemainingHours(selectedEmployee, selectedDate, selectedShop ?? undefined) +
      (selectedWorkingHours?.duration || 0);
    if (workingHoursForm.duration > maxAvailableHours) {
      durationError = `Exceeds available hours (${maxAvailableHours.toFixed(
        1
      )}h)`;
    }

    // Validate duration doesn't go past shop closing time (using already retrieved shopHours)
    if (shopHours && shopHours.isOpen) {
      const [startHour, startMin] = workingHoursForm.startTime
        .split(":")
        .map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = startMinutes + workingHoursForm.duration * 60;

      const [closeHour, closeMin] = shopHours.closeTime.split(":").map(Number);
      let closeMinutes = closeHour * 60 + closeMin;

      // Handle shops that close after midnight
      if (closeMinutes <= startMinutes) {
        closeMinutes += 24 * 60;
      }

      if (endMinutes > closeMinutes) {
        durationError = `Shift ends after shop closes (${shopHours.closeTime})`;
      }
    }

    // Validate minimum duration
    if (workingHoursForm.duration <= 0) {
      durationError = "Duration must be greater than 0";
    }

    return {
      isValid: !startTimeError && !durationError,
      startTimeError,
      durationError,
    };
  };

  const formValidation = validateWorkingHoursForm();

  // Calculate optimal duration based on start time (using dialog state)
  const calculateOptimalDuration = (startTime: string): number => {
    if (!selectedShop || !selectedEmployee || !selectedDate) return 0;

    return calculateOptimalDurationFor(
      startTime,
      selectedShop,
      selectedEmployee,
      selectedDate,
      selectedWorkingHours?.duration
    );
  };

  // Handle start time change
  const handleStartTimeChange = (newStartTime: string) => {
    const optimalDuration = calculateOptimalDuration(newStartTime);
    setWorkingHoursForm({
      ...workingHoursForm,
      startTime: newStartTime,
      duration: optimalDuration,
    });
  };

  // Handle save working hours (updates local state, not Firebase directly)
  const handleSaveWorkingHours = () => {
    if (!selectedShop || !selectedEmployee || !selectedDate) return;
    if (!formValidation.isValid) return;

    // Get current working hours for this cell
    const currentWorkingHours = getEmployeeWorkingHoursArray(
      selectedEmployee.id,
      selectedShop.id,
      selectedDate
    );

    let newWorkingHours: WorkingHours[];

    if (selectedWorkingHours) {
      // Editing existing entry - replace it
      newWorkingHours = currentWorkingHours.map((wh) =>
        wh.id === selectedWorkingHours.id ? workingHoursForm : wh
      );
    } else {
      // Adding new entry
      newWorkingHours = [...currentWorkingHours, workingHoursForm];
    }

    // Close dialog first to prevent flash of validation error during state update
    setDialogOpen(false);

    // Update local state
    addLocalChange(
      selectedShop.id,
      selectedEmployee.id,
      selectedEmployee.name,
      selectedDate,
      newWorkingHours
    );

    // Use centralized validation to recalculate errors
    // Pass newWorkingHours since state hasn't updated yet
    recalculateErrorsForEmployeeDate(selectedEmployee, selectedDate, {
      shopId: selectedShop.id,
      workingHours: newWorkingHours,
    });
  };

  // Handle delete working hours (updates local state, not Firebase directly)
  const handleDeleteWorkingHours = () => {
    if (
      !selectedShop ||
      !selectedEmployee ||
      !selectedDate ||
      !selectedWorkingHours
    )
      return;

    // Get current working hours for this cell
    const currentWorkingHours = getEmployeeWorkingHoursArray(
      selectedEmployee.id,
      selectedShop.id,
      selectedDate
    );

    // Remove the selected working hours
    const newWorkingHours = currentWorkingHours.filter(
      (wh) => wh.id !== selectedWorkingHours.id
    );

    // Close dialog first to prevent flash of validation error during state update
    setDialogOpen(false);

    // Update local state
    addLocalChange(
      selectedShop.id,
      selectedEmployee.id,
      selectedEmployee.name,
      selectedDate,
      newWorkingHours
    );

    // Use centralized validation to recalculate errors
    // Pass newWorkingHours since state hasn't updated yet
    recalculateErrorsForEmployeeDate(selectedEmployee, selectedDate, {
      shopId: selectedShop.id,
      workingHours: newWorkingHours,
    });
  };

  // Export schedule data to CSV
  const handleExportCSV = () => {
    // CSV header
    const headers = ["Shop", "Employee", "Date", "Day", "Start Time", "End Time", "Duration (hours)"];

    // Collect all schedule data
    const rows: string[][] = [];

    shops.forEach((shop) => {
      const shopEmployees = employees.filter(
        (emp) =>
          emp.status === "active" &&
          emp.assignedShopIds.includes(shop.id)
      );

      shopEmployees.forEach((employee) => {
        weekDates.forEach((date, dateIndex) => {
          const workingHoursArray = getEmployeeWorkingHoursArray(
            employee.id,
            shop.id,
            date
          );

          workingHoursArray.forEach((wh) => {
            const dayName = dayLabels[dayOfWeekOrder[dateIndex]];
            const dateStr = formatDate(date);
            const endTime = calculateEndTime(wh.startTime, wh.duration);

            rows.push([
              shop.name,
              employee.name,
              dateStr,
              dayName,
              wh.startTime,
              endTime,
              wh.duration.toString(),
            ]);
          });
        });
      });
    });

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    // Create download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    // Generate filename with date range
    const startDateStr = formatDate(weekDates[0]);
    const endDateStr = formatDate(weekDates[6]);
    const filename = `schedule_${startDateStr}_to_${endDateStr}.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    enqueueSnackbar("Schedule exported to CSV", { variant: "success" });
  };

  // Export schedule data to PDF
  const handleExportPDF = () => {
    // Create PDF document (landscape for better table fit)
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // Theme colors (Ocean Teal)
    const primaryColor: [number, number, number] = [0, 137, 123]; // Teal
    const primaryLight: [number, number, number] = [224, 242, 241]; // Light teal
    const textDark: [number, number, number] = [33, 33, 33];
    const textMuted: [number, number, number] = [117, 117, 117];
    const borderColor: [number, number, number] = [224, 224, 224];
    const white: [number, number, number] = [255, 255, 255];
    const rowAlt: [number, number, number] = [250, 250, 250];

    // Get page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    // Generate filename and title with date range
    const startDateStr = formatDate(weekDates[0]);
    const endDateStr = formatDate(weekDates[6]);
    const weekNumber = getWeekNumber(weekDates[0]);

    // Helper function to draw the header on each page
    const drawHeader = (pageNum: number, totalPages: number) => {
      // Top accent bar
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 4, "F");

      // Logo/Brand area
      doc.setFillColor(...primaryColor);
      doc.roundedRect(margin, 10, 10, 10, 2, 2, "F");
      doc.setTextColor(...white);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("S", margin + 3.5, 17);

      // Title
      doc.setTextColor(...textDark);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Weekly Schedule", margin + 14, 17);

      // Organization name badge
      if (organization?.name) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textMuted);
        doc.text(organization.name, margin + 14, 22);
      }

      // Week info box (right side)
      const boxWidth = 70;
      const boxX = pageWidth - margin - boxWidth;
      doc.setFillColor(...primaryLight);
      doc.roundedRect(boxX, 8, boxWidth, 18, 3, 3, "F");

      // Week number
      doc.setTextColor(...primaryColor);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`WEEK ${weekNumber}`, boxX + boxWidth / 2, 14, { align: "center" });

      // Date range
      doc.setTextColor(...textDark);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const startDate = weekDates[0];
      const endDate = weekDates[6];
      const dateText = `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
      doc.text(dateText, boxX + boxWidth / 2, 21, { align: "center" });

      // Divider line
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.5);
      doc.line(margin, 30, pageWidth - margin, 30);

      // Page number (top right, subtle)
      doc.setFontSize(8);
      doc.setTextColor(...textMuted);
      doc.text(`${pageNum} / ${totalPages}`, pageWidth - margin, 6, { align: "right" });
    };

    // Helper function to draw footer
    const drawFooter = () => {
      const footerY = pageHeight - 8;

      // Footer line
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 3, pageWidth - margin, footerY - 3);

      // Generated timestamp
      doc.setFontSize(7);
      doc.setTextColor(...textMuted);
      doc.setFont("helvetica", "normal");
      const now = new Date();
      doc.text(
        `Generated ${now.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })} at ${now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`,
        margin,
        footerY
      );

      // App branding
      doc.text("ShiftGrid", pageWidth - margin, footerY, { align: "right" });
    };

    let yPosition = 38;
    let isFirstShop = true;

    // Process each shop
    shops.forEach((shop) => {
      const shopEmployees = employees.filter(
        (emp) =>
          emp.status === "active" &&
          emp.assignedShopIds.includes(shop.id)
      );

      if (shopEmployees.length === 0) return;

      // Check if we need a new page
      if (yPosition > 160) {
        doc.addPage();
        yPosition = 38;
        isFirstShop = true;
      }

      // Shop section header with colored accent
      if (!isFirstShop) {
        yPosition += 5;
      }

      // Shop name with accent
      doc.setFillColor(...primaryColor);
      doc.roundedRect(margin, yPosition - 4, 3, 10, 1, 1, "F");

      doc.setTextColor(...textDark);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(shop.name, margin + 6, yPosition + 2);

      // Calculate shop name width while font is still size 12
      const shopNameWidth = doc.getTextWidth(shop.name);

      // Employee count badge
      const empCountText = `${shopEmployees.length} employee${shopEmployees.length > 1 ? "s" : ""}`;
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...textMuted);
      doc.text(empCountText, margin + 10 + shopNameWidth, yPosition + 2);

      yPosition += 8;

      // Prepare table data
      const tableHead = [
        ["Employee", ...weekDates.map((date, idx) => {
          const dayName = dayLabels[dayOfWeekOrder[idx]];
          const dateNum = date.getDate();
          const month = date.toLocaleDateString("en-US", { month: "short" });
          return `${dayName}\n${month} ${dateNum}`;
        })],
      ];

      // Data rows
      const tableBody = shopEmployees.map((employee) => {
        const row = [employee.name];

        weekDates.forEach((date) => {
          const workingHoursArray = getEmployeeWorkingHoursArray(
            employee.id,
            shop.id,
            date
          );

          if (workingHoursArray.length === 0) {
            row.push("—");
          } else {
            const shifts = workingHoursArray.map((wh) => {
              const endTime = calculateEndTime(wh.startTime, wh.duration);
              return `${wh.startTime} - ${endTime}`;
            });
            row.push(shifts.join("\n"));
          }
        });

        return row;
      });

      // Generate modern table
      autoTable(doc, {
        startY: yPosition,
        head: tableHead,
        body: tableBody,
        theme: "plain",
        styles: {
          fontSize: 8,
          cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
          lineColor: borderColor,
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: primaryColor,
          textColor: white,
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          fontSize: 8,
          cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
        },
        bodyStyles: {
          halign: "center",
          valign: "middle",
          textColor: textDark,
        },
        columnStyles: {
          0: {
            halign: "left",
            fontStyle: "bold",
            cellWidth: 38,
            fillColor: primaryLight,
            textColor: textDark,
          },
        },
        alternateRowStyles: {
          fillColor: rowAlt,
        },
        margin: { left: margin, right: margin },
        tableWidth: "auto",
        didParseCell: (data) => {
          // Style empty cells differently
          if (data.section === "body" && data.column.index > 0) {
            const cellText = data.cell.raw as string;
            if (cellText === "—") {
              data.cell.styles.textColor = [189, 189, 189];
              data.cell.styles.fontStyle = "normal";
            } else if (cellText && cellText !== "—") {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.textColor = primaryColor;
            }
          }
        },
      });

      // Update yPosition for next shop
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yPosition = (doc as any).lastAutoTable.finalY + 8;
      isFirstShop = false;
    });

    // Add headers and footers to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawHeader(i, totalPages);
      drawFooter();
    }

    // Save the PDF
    const filename = `schedule_${startDateStr}_to_${endDateStr}.pdf`;
    doc.save(filename);

    enqueueSnackbar("Schedule exported to PDF", { variant: "success" });
  };

  // Wrapper component to conditionally use Layout
  const Wrapper = skipLayout
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : ({ children }: { children: React.ReactNode }) => (
        <Layout pageTitle={view === "employee" ? "Employee Schedules" : "Shop Coverage"}>
          {children}
        </Layout>
      );

  if (isLoadingShops || isLoadingEmployees) {
    return (
      <Wrapper>
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  if (shops.length === 0) {
    return (
      <Wrapper>
        <Paper sx={{ p: 8, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No shops found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add shops to start managing schedules
          </Typography>
        </Paper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <>
        {/* Left Sidebar (Employee View Only, not in read-only mode) */}
        {view === "employee" && !readOnly && (
          <SchedulesSidebar
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            shops={shops}
            hasChanges={hasUnsavedChanges()}
            changesCount={getUnsavedChangesCount()}
            hasErrors={hasErrors()}
            errorsCount={getErrorsCount()}
            localChanges={localChanges}
            onSave={saveAllChanges}
            onRevertAll={revertAllChanges}
            onRevertChange={revertCellChange}
            isSaving={isSaving}
            copiedCellData={copiedCellData}
            copiedEmployeeWeekData={copiedEmployeeWeekData}
            copiedShopWeekData={copiedShopWeekData}
            copiedEntireWeekData={copiedEntireWeekData}
            onCopyWeek={handleCopyEntireWeek}
            onPasteWeek={handlePasteEntireWeek}
            onClearClipboard={clearAllClipboard}
            onExportPDF={handleExportPDF}
            onExportCSV={handleExportCSV}
          />
        )}

        {/* Main Content with left margin for sidebar (no margin in read-only mode) */}
        <Box
          sx={{
            ml: view === "employee" && !readOnly ? (sidebarOpen ? "280px" : "64px") : 0,
          }}
        >
          {/* Employee Schedule View */}
          {view === "employee" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "100%",
              }}
            >
              {/* Week Navigator */}
              <DateNavigator
                mode="weekly"
                selectedDate={formatDate(weekStart)}
                onDateChange={handleDateChange}
              />

              {/* Schedule Table */}
              <TableContainer
                component={Paper}
                sx={{ mt: 2, flexGrow: 1, overflow: "auto" }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          width: 200,
                          position: "sticky",
                          left: 0,
                          bgcolor: "background.paper",
                          zIndex: 3,
                        }}
                      >
                        Shop / Employee
                      </TableCell>
                      {weekDates.map((date, index) => {
                        const day = dayOfWeekOrder[index];
                        const isTodayCell = isToday(date);
                        return (
                          <TableCell
                            key={day}
                            align="center"
                            sx={{
                              fontWeight: 700,
                              minWidth: 150,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={
                                isTodayCell ? "primary.main" : "text.primary"
                              }
                            >
                              {dayLabels[day]}
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color={
                                isTodayCell ? "primary.main" : "text.primary"
                              }
                            >
                              {date.getDate()}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shops.map((shop) => {
                      // Get employees assigned to this shop
                      const shopEmployees = employees.filter(
                        (emp) =>
                          emp.status === "active" &&
                          emp.assignedShopIds.includes(shop.id)
                      );

                      return (
                        <Fragment key={shop.id}>
                          {/* Shop header row */}
                          <TableRow>
                            <TableCell
                              sx={{
                                bgcolor: "grey.100",
                                fontWeight: 700,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    bgcolor: shop.color,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  fontWeight={700}
                                  sx={{ mr: 0.5 }}
                                >
                                  {shop.name}
                                </Typography>
                                {/* Copy shop week button (hidden in read-only mode) */}
                                {!readOnly && (
                                  <Tooltip title="Copy shop schedule">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyShopWeek(shop)}
                                      sx={{ width: 24, height: 24 }}
                                    >
                                      <ContentCopy sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {/* Paste shop week button (hidden in read-only mode) */}
                                {!readOnly && copiedShopWeekData && (
                                  <Tooltip
                                    title={`Paste from ${copiedShopWeekData.shopName}`}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePasteShopWeek(shop)}
                                      sx={{ width: 24, height: 24 }}
                                      color="info"
                                    >
                                      <ContentPaste sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            {/* Coverage requirements for each day */}
                            {weekDates.map((date, dateIndex) => {
                              const coverageReqs = getCoverageForDate(
                                shop,
                                date
                              );
                              const dateKey = formatDate(date);
                              const schedule = getScheduleForDate(dateKey, shop.id);
                              const scheduledInDifferentMode =
                                schedule &&
                                schedule.availabilityMode &&
                                schedule.availabilityMode !== availabilityMode;

                              return (
                                <TableCell
                                  key={dateIndex}
                                  align="center"
                                  sx={{
                                    bgcolor: "grey.100",
                                    py: 0.5,
                                  }}
                                >
                                  {/* Mode indicator */}
                                  {scheduledInDifferentMode && (
                                    <Tooltip
                                      title={`Scheduled in ${schedule.availabilityMode} mode`}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: 0.5,
                                          mb: 0.25,
                                        }}
                                      >
                                        <Info
                                          sx={{
                                            fontSize: 14,
                                            color: "info.main",
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.65rem",
                                            color: "info.main",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {schedule.availabilityMode}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  )}
                                  {/* Coverage gaps indicator (hidden in read-only mode) */}
                                  {!readOnly && coverageReqs.length > 0 ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.25,
                                      }}
                                    >
                                      {(() => {
                                        const gaps = getShopDayCoverageGaps(shop, date);
                                        if (gaps.length === 0) {
                                          return (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 0.5,
                                                py: 0.25,
                                                px: 0.75,
                                                borderRadius: 1,
                                                bgcolor: 'success.50',
                                              }}
                                            >
                                              <CheckCircle sx={{ fontSize: 12, color: 'success.main' }} />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize: "0.65rem",
                                                  fontWeight: 600,
                                                  color: 'success.main',
                                                }}
                                              >
                                                Covered
                                              </Typography>
                                            </Box>
                                          );
                                        }
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: 0.25,
                                            }}
                                          >
                                            {gaps.map((gap, idx) => (
                                              <Tooltip
                                                key={idx}
                                                title={`Need ${gap.shortage} more (${gap.required} required)`}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 0.5,
                                                    py: 0.25,
                                                    px: 0.75,
                                                    borderRadius: 1,
                                                    bgcolor: 'error.50',
                                                  }}
                                                >
                                                  <Error sx={{ fontSize: 10, color: 'error.main' }} />
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      fontSize: "0.6rem",
                                                      fontWeight: 600,
                                                      color: 'error.main',
                                                    }}
                                                  >
                                                    {gap.timeSlot}: +{gap.shortage}
                                                  </Typography>
                                                </Box>
                                              </Tooltip>
                                            ))}
                                          </Box>
                                        );
                                      })()}
                                    </Box>
                                  ) : !readOnly ? (
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                    >
                                      -
                                    </Typography>
                                  ) : null}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {/* Employee rows */}
                          {shopEmployees.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={8}
                                sx={{
                                  textAlign: "center",
                                  fontStyle: "italic",
                                  color: "text.secondary",
                                }}
                              >
                                No employees assigned to this shop
                              </TableCell>
                            </TableRow>
                          ) : (
                            shopEmployees.map((employee) => (
                              <TableRow key={`employee-${employee.id}`} hover>
                                <TableCell
                                  sx={{
                                    position: "sticky",
                                    left: 0,
                                    bgcolor: "background.paper",
                                    zIndex: 1,
                                    pl: 4,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ mr: 0.5 }}
                                      >
                                        {employee.name}
                                      </Typography>
                                      {/* Weekly availability display (hidden in read-only mode) */}
                                      {!readOnly && isWeeklyMode && (
                                        <Typography
                                          variant="caption"
                                          color={
                                            getEmployeeWeeklyRemainingHours(employee) > 0
                                              ? "success.main"
                                              : getEmployeeWeeklyRemainingHours(employee) < 0
                                              ? "error.main"
                                              : "text.secondary"
                                          }
                                          fontWeight={600}
                                        >
                                          {getEmployeeWeeklyRemainingHours(employee) > 0
                                            ? `${getEmployeeWeeklyRemainingHours(employee).toFixed(1)}h left`
                                            : getEmployeeWeeklyRemainingHours(employee) < 0
                                            ? `${Math.abs(getEmployeeWeeklyRemainingHours(employee)).toFixed(1)}h over`
                                            : "Fully scheduled"}
                                        </Typography>
                                      )}
                                    </Box>
                                    {/* Copy week button (hidden in read-only mode) */}
                                    {!readOnly && (
                                      <Tooltip title="Copy week schedule">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleCopyEmployeeWeek(shop, employee)
                                          }
                                          sx={{ width: 20, height: 20 }}
                                        >
                                          <ContentCopy sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {/* Paste week button (hidden in read-only mode) */}
                                    {!readOnly && copiedEmployeeWeekData && (
                                      <Tooltip
                                        title={`Paste week from ${copiedEmployeeWeekData.employeeName}`}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handlePasteEmployeeWeek(
                                              shop,
                                              employee
                                            )
                                          }
                                          sx={{ width: 20, height: 20 }}
                                          color="info"
                                        >
                                          <ContentPaste sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {/* Revert row button (hidden in read-only mode) */}
                                    {!readOnly && employeeRowHasChanges(
                                      shop.id,
                                      employee.id
                                    ) && (
                                      <Tooltip title="Revert all changes for this employee">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            revertEmployeeRowChanges(
                                              shop.id,
                                              employee.id
                                            )
                                          }
                                          sx={{
                                            width: 20,
                                            height: 20,
                                            color: "warning.main",
                                          }}
                                        >
                                          <Undo sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                {weekDates.map((date, dateIndex) => {
                                  const workingHoursArray =
                                    getEmployeeWorkingHoursArray(
                                      employee.id,
                                      shop.id,
                                      date
                                    );
                                  const remainingHours = getRemainingHours(
                                    employee,
                                    date,
                                    shop
                                  );
                                  const isOverScheduled = remainingHours < 0;
                                  const hasCellChanges = cellHasChanges(
                                    shop.id,
                                    employee.id,
                                    date
                                  );
                                  const canPaste = remainingHours > 0;

                                  return (
                                    <TableCell
                                      key={dateIndex}
                                      align="center"
                                      sx={{
                                        bgcolor: "background.paper",
                                        verticalAlign: "top",
                                        py: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                          alignItems: "center",
                                        }}
                                      >
                                        {/* Show all working hours entries */}
                                        {workingHoursArray.map((wh) => {
                                          const isChanged =
                                            isWorkingHoursChanged(
                                              shop.id,
                                              employee.id,
                                              date,
                                              wh.id
                                            );
                                          const error = getWorkingHoursError(
                                            shop.id,
                                            employee.id,
                                            formatDate(date),
                                            wh.id
                                          );
                                          const hasError = !!error;

                                          return (
                                            <Tooltip
                                              key={wh.id}
                                              title={!readOnly && hasError ? error : ""}
                                              placement="top"
                                            >
                                              <Chip
                                                label={`${
                                                  wh.startTime
                                                } - ${calculateEndTime(
                                                  wh.startTime,
                                                  wh.duration
                                                )}`}
                                                size="small"
                                                color={
                                                  !readOnly && hasError
                                                    ? "error"
                                                    : !readOnly && isChanged
                                                    ? "warning"
                                                    : "primary"
                                                }
                                                onClick={
                                                  !readOnly && canEditDate(date)
                                                    ? () =>
                                                        handleOpenDialog(
                                                          shop,
                                                          employee,
                                                          date,
                                                          wh
                                                        )
                                                    : undefined
                                                }
                                                onDelete={
                                                  !readOnly && canEditDate(date) && hasError
                                                    ? () =>
                                                        removeWorkingHoursWithError(
                                                          shop.id,
                                                          employee.id,
                                                          date,
                                                          wh.id
                                                        )
                                                    : undefined
                                                }
                                                deleteIcon={
                                                  !readOnly && canEditDate(date) && hasError ? (
                                                    <Close />
                                                  ) : undefined
                                                }
                                                sx={{ cursor: !readOnly && canEditDate(date) ? "pointer" : "default" }}
                                              />
                                            </Tooltip>
                                          );
                                        })}

                                        {/* Show remaining hours or over-scheduled warning (hidden in read-only mode) */}
                                        {/* Only show "left" if positive remaining */}
                                        {!readOnly && remainingHours > 0 && (
                                          <Typography
                                            variant="caption"
                                            color="success.main"
                                            fontWeight={600}
                                          >
                                            {remainingHours.toFixed(1)}h left
                                          </Typography>
                                        )}
                                        {/* Only show "over" in cells with unsaved changes (hidden in read-only mode) */}
                                        {!readOnly && isOverScheduled && hasCellChanges && (
                                          <Typography
                                            variant="caption"
                                            color="error.main"
                                            fontWeight={600}
                                          >
                                            {Math.abs(remainingHours).toFixed(1)}h over
                                          </Typography>
                                        )}

                                        {/* Show action buttons */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            flexWrap: "wrap",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {/* Add button (hidden in read-only mode) */}
                                          {!readOnly && canEditDate(date) && remainingHours > 0 && (
                                            <Tooltip title="Add working hours">
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                  handleOpenDialog(
                                                    shop,
                                                    employee,
                                                    date
                                                  )
                                                }
                                                sx={{ width: 24, height: 24 }}
                                              >
                                                <Add fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          {/* Copy cell button (hidden in read-only mode) */}
                                          {!readOnly && workingHoursArray.length > 0 && (
                                            <Tooltip title="Copy day schedule">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleCopyCell(
                                                    shop,
                                                    employee,
                                                    date
                                                  )
                                                }
                                                sx={{ width: 24, height: 24 }}
                                              >
                                                <ContentCopy
                                                  sx={{ fontSize: 14 }}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          {/* Paste cell button (hidden in read-only mode) */}
                                          {!readOnly && canEditDate(date) && copiedCellData && canPaste && (
                                            <Tooltip
                                              title={`Paste from ${copiedCellData.employeeName}`}
                                            >
                                              <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() =>
                                                  handlePasteCell(
                                                    shop,
                                                    employee,
                                                    date
                                                  )
                                                }
                                                sx={{ width: 24, height: 24 }}
                                              >
                                                <ContentPaste
                                                  sx={{ fontSize: 14 }}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          {/* Revert button */}
                                          {hasCellChanges && (
                                            <Tooltip title="Revert changes">
                                              <IconButton
                                                size="small"
                                                color="warning"
                                                onClick={() =>
                                                  revertCellChange(
                                                    shop.id,
                                                    employee.id,
                                                    date
                                                  )
                                                }
                                                sx={{ width: 24, height: 24 }}
                                              >
                                                <Undo fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>

                                        {/* Show dash if no hours and no availability */}
                                        {workingHoursArray.length === 0 &&
                                          remainingHours === 0 &&
                                          !hasCellChanges && (
                                            <Typography
                                              variant="caption"
                                              color="text.disabled"
                                            >
                                              -
                                            </Typography>
                                          )}
                                      </Box>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Shop Coverage View */}
          {view === "coverage" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "100%",
              }}
            >
              {/* Coverage Table - includes fixed sidebar */}
              <CoverageTable
                shops={shops}
                weekDates={weekDates}
                employees={employees}
                getScheduleForDate={getScheduleForDate}
                weekStart={weekStart}
                onDateChange={handleDateChange}
                allowEditHistory={allowEditHistory}
                onAllocateEmployee={async (
                  shopId,
                  date,
                  employeeId,
                  employeeName,
                  workingHours
                ) => {
                  // Get or create schedule
                  let schedule = getScheduleForDate(date, shopId);
                  let scheduleId: string;

                  if (!schedule) {
                    scheduleId = await createSchedule({
                      shopId,
                      date,
                      employeeSchedules: [],
                      availabilityMode,
                    });
                    await refetch();
                  } else {
                    scheduleId = schedule.id;
                    // Update existing schedule with current availability mode
                    await updateSchedule(scheduleId, { availabilityMode });
                  }

                  // Add working hours
                  await addOrUpdateEmployeeWorkingHours(
                    scheduleId,
                    employeeId,
                    employeeName,
                    workingHours
                  );

                  await refetch();
                }}
              />
            </Box>
          )}
        </Box>

        {/* Working Hours Dialog */}
        <SchedulesDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          selectedShop={selectedShop}
          selectedEmployee={selectedEmployee}
          selectedDate={selectedDate}
          selectedWorkingHours={selectedWorkingHours}
          startTime={workingHoursForm.startTime}
          duration={workingHoursForm.duration}
          onStartTimeChange={handleStartTimeChange}
          onDurationChange={(duration) =>
            setWorkingHoursForm({ ...workingHoursForm, duration })
          }
          formValidation={formValidation}
          onSave={handleSaveWorkingHours}
          onDelete={handleDeleteWorkingHours}
          availableHours={
            selectedEmployee && selectedDate
              ? getRemainingHours(selectedEmployee, selectedDate, selectedShop ?? undefined) +
                (selectedWorkingHours?.duration || 0)
              : 0
          }
        />
      </>
    </Wrapper>
  );
}
